import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Field from "@/components/billing/Field";

/**
 * MSCreateDialog — Measurement-sheet create dialog extracted from RABilling.jsx.
 *
 * Props:
 *   • boqs   — list of BOQs to pick from
 *   • onClose / onSaved — parent lifecycle callbacks
 */
export default function MSCreateDialog({ boqs, onClose, onSaved }) {
  const [form, setForm] = useState({
    boq_id: "", sheet_no: "", sheet_date: new Date().toISOString().slice(0, 10),
    signed_by_client: false, signed_by_client_name: "", lines: [], notes: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const boq = useMemo(() => boqs.find((b) => b.id === form.boq_id), [boqs, form.boq_id]);
  useEffect(() => {
    if (boq) {
      (async () => {
        try {
          const { data } = await api.get(`/boqs/${boq.id}`);
          setForm((f) => ({ ...f, lines: (data.items || []).map((it, i) => ({ boq_item_index: i, description: it.description, measured_qty: 0, milestone_hint: "install", remarks: "" })) }));
        } catch (e) { devLog.warn(e); }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.boq_id]);
  const patch = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((ln, idx) => idx === i ? { ...ln, [k]: v } : ln) }));

  const save = async () => {
    if (!form.boq_id) return toast.error("Pick a BOQ");
    setBusy(true);
    try {
      let attachment_id = "";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/attachments/upload?parent_type=measurement_sheet&parent_id=temp&category=measurement_sheet`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_id = data.id;
      }
      await api.post("/measurement-sheets", { ...form, attachment_id });
      toast.success("Measurement sheet saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[min(900px,100vw)] sm:max-w-none overflow-y-auto" data-testid="ms-sheet">
        <SheetHeader className="mb-4"><SheetTitle className="font-display">New Measurement Sheet</SheetTitle></SheetHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="BOQ *">
              <Select value={form.boq_id} onValueChange={(v) => setForm({ ...form, boq_id: v })}>
                <SelectTrigger data-testid="ms-boq"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent>{boqs.map((b) => <SelectItem key={b.id} value={b.id}>{b.boq_no} · {b.boq_title}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Sheet # (blank = auto)"><Input value={form.sheet_no} onChange={(e) => setForm({ ...form, sheet_no: e.target.value })} className="font-mono" /></Field>
            <Field label="Sheet Date"><Input type="date" value={form.sheet_date} onChange={(e) => setForm({ ...form, sheet_date: e.target.value })} /></Field>
            <Field label="Signed by client?">
              <Select value={form.signed_by_client ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, signed_by_client: v === "yes" })}>
                <SelectTrigger data-testid="ms-signed"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes · client-signed</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Client signer name"><Input value={form.signed_by_client_name} onChange={(e) => setForm({ ...form, signed_by_client_name: e.target.value })} /></Field>
            <Field label="Attach signed file (PDF/image)"><Input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ms-file" /></Field>
          </div>
          {form.lines.length > 0 && (
            <div className="border border-slate-200 rounded-sm">
              <Table className="bc-list">
                <TableHeader><TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-32">Milestone</TableHead>
                  <TableHead className="text-right w-32">Measured Qty</TableHead>
                  <TableHead>Remarks</TableHead>
                </TableRow></TableHeader>
                <TableBody>{form.lines.map((ln, i) => (
                  <TableRow key={`ms-line-${ln.boq_item_index ?? ln.description ?? i}`}>
                    <TableCell className="text-[12px]">{ln.description}</TableCell>
                    <TableCell>
                      <Select value={ln.milestone_hint || "install"} onValueChange={(v) => patch(i, "milestone_hint", v)}>
                        <SelectTrigger className="h-7 text-[11px]" data-testid={`ms-hint-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="supply">Supply</SelectItem>
                          <SelectItem value="install">Installation</SelectItem>
                          <SelectItem value="tnc">Testing / T&C</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><Input type="number" value={ln.measured_qty} onChange={(e) => patch(i, "measured_qty", Number(e.target.value) || 0)} className="h-7 text-right font-mono-num" data-testid={`ms-qty-${i}`} /></TableCell>
                    <TableCell><Input value={ln.remarks} onChange={(e) => patch(i, "remarks", e.target.value)} className="h-7" /></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </div>
          )}
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={busy} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="ms-save"><FloppyDisk size={13} className="mr-1" /> {busy ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
